import type {
  OpticalMeasureSettings,
  SHCaptureAggregateData,
} from '../../src/ExpoOpticalMeasure.types'
import type { ShTestingSettingsSelection } from '../types'

/**
 * Session-scoped record of completed captures, and the one statistic a bench
 * repeat set exists to produce: how much of the between-capture spread
 * per-frame noise actually explains.
 *
 * A single capture's aggregate cannot answer that — it only describes what
 * varied *inside* one capture. The question is whether the reported value moves
 * more between captures than that within-capture noise predicts. If it does, no
 * estimator change (A1, A2, A5) can help, because they all average per-frame
 * noise and the variance is not there.
 */

/**
 * The settings a set was actually captured under.
 *
 * Recorded because a log without it is not self-describing, and we learned that
 * the expensive way: one set was analysed at length to work out which distance
 * band produced it, and the answer turned out to be a testing-settings chip
 * (`tight`, 21–49 cm) that nobody had thought to mention. Every conclusion
 * drawn from a set depends on the configuration it ran under, so the two travel
 * together.
 */
export type ShCaptureLogConfig = {
  selection: ShTestingSettingsSelection
  isModified: boolean
  profile: 'vbox' | 'normal'
  preferredDistanceCm: number
  guidanceDistanceToleranceCm: number
  captureDistanceToleranceCm: number
  ovalPaddingFactor: number
  maxRotationXDeg: number
  rotationXOffsetDeg: number
  targetCandidateFrames: number
  spreadMetric: string
  stabilityGateMm: number
  lensDepthMm: number
}

/**
 * Stable identity of the configuration a capture ran under, so a set taken
 * under one set of settings cannot be summarised as though it were another.
 *
 * The log survives Repeat, and so does the settings modal — change a row
 * mid-session and every later capture is measuring something else. Without a
 * key the summary silently pools both and the config header claims whichever
 * settings happen to be current, which is the same failure as an unlabelled
 * log, one level in.
 *
 * Keyed rather than cleared on change, because switching back is the A/B
 * workflow: run ten with the correction off, ten with it on, and each side
 * keeps its own set.
 *
 * Built from the resolved numbers, not the selection chips — two different
 * settings objects can land on the same chips, and it is the numbers that
 * moved the measurement.
 */
export const shCaptureLogConfigKey = (config: ShCaptureLogConfig): string =>
  [
    config.profile,
    config.preferredDistanceCm,
    config.guidanceDistanceToleranceCm,
    config.captureDistanceToleranceCm,
    config.ovalPaddingFactor,
    config.maxRotationXDeg,
    config.rotationXOffsetDeg,
    config.targetCandidateFrames,
    config.spreadMetric,
    config.stabilityGateMm,
    config.lensDepthMm,
  ].join('|')

export const shCaptureLogConfig = (
  settings: OpticalMeasureSettings,
  selection: ShTestingSettingsSelection,
  isModified: boolean
): ShCaptureLogConfig => {
  const usesVbox = settings.sh.scale.vboxMm > 0
  const profile = usesVbox ? settings.framing.vbox : settings.framing.normal
  return {
    selection,
    isModified,
    profile: usesVbox ? 'vbox' : 'normal',
    preferredDistanceCm: profile.preferredDistanceCm,
    guidanceDistanceToleranceCm: profile.guidanceDistanceToleranceCm,
    captureDistanceToleranceCm: profile.captureDistanceToleranceCm,
    ovalPaddingFactor: settings.framing.faceFit.ovalPaddingFactor,
    maxRotationXDeg: settings.sh.gates.maxRotationXDeg,
    rotationXOffsetDeg: settings.geometry.rotationXOffsetDeg,
    targetCandidateFrames: settings.sh.capture.targetCandidateFrames,
    spreadMetric: settings.sh.capture.spreadMetric,
    stabilityGateMm:
      settings.sh.capture.spreadMetric === 'mad'
        ? settings.sh.capture.maxSampleDispersionMm
        : settings.sh.capture.maxSampleSpreadMm,
    lensDepthMm: settings.sh.correction.lensDepthMm,
  }
}

/**
 * The entries taken under `config`, plus how many were not. The count is
 * surfaced rather than dropped: a set that quietly shrank when a row was
 * touched is as misleading as one that quietly pooled.
 */
export const selectShCaptureLogEntries = (
  entries: ShCaptureLogEntry[],
  config: ShCaptureLogConfig
): { active: ShCaptureLogEntry[]; otherCount: number } => {
  const key = shCaptureLogConfigKey(config)
  const active = entries.filter((entry) => entry.configKey === key)
  return { active, otherCount: entries.length - active.length }
}

/**
 * Three lines, in the order they answer questions: which chips were selected,
 * where the two distance cues actually point, and what the gates were.
 *
 * The oval line carries `implies`, the distance a subject lands at by filling
 * the oval (`preferred / padding`). When it disagrees with the band's target,
 * the oval wins — it is the cue they can see themselves against.
 */
export const formatShCaptureLogConfig = (
  config: ShCaptureLogConfig
): string[] => {
  const { selection } = config
  const impliesCm = config.preferredDistanceCm / (config.ovalPaddingFactor || 1)
  const trueLow = -config.maxRotationXDeg - config.rotationXOffsetDeg
  const trueHigh = config.maxRotationXDeg - config.rotationXOffsetDeg
  return [
    `config  ${selection.distanceTolerance} bands · baseline ${
      selection.baseline
    } · ${selection.scaleReference} · ${selection.pitchReference} · tilt-corr ${
      selection.pitchCorrection
    } · n${config.targetCandidateFrames}${
      config.isModified ? '   [MODIFIED]' : ''
    }`,
    `        ${config.profile} ${config.preferredDistanceCm} cm ±${
      config.guidanceDistanceToleranceCm
    } guide / ±${
      config.captureDistanceToleranceCm
    } capture · oval ×${config.ovalPaddingFactor.toFixed(
      2
    )} implies ${impliesCm.toFixed(1)} cm`,
    `        pose ±${config.maxRotationXDeg}° at offset ${
      config.rotationXOffsetDeg >= 0 ? '+' : ''
    }${config.rotationXOffsetDeg}° (true ${trueLow}…${trueHigh}°) · ${
      config.spreadMetric
    } gate ${config.stabilityGateMm}`,
  ]
}

export type ShCaptureLogEntry = {
  /** Native capture identity; the log keeps one entry per session. */
  sessionId: number
  /** The configuration this capture ran under — see `shCaptureLogConfigKey`. */
  configKey: string
  left: number
  right: number
  sampleCount: number
  dispersionLeftMm?: number
  dispersionRightMm?: number
  mmPerPixelMean?: number
  scaleCv?: number
  distanceMeanCm?: number
  pitchMeanDeg?: number
  gazeMeanDeg?: number
  scaleSource?: string
  scaleSourceSwitched?: boolean
}

/**
 * Which per-capture input the reported value actually tracks.
 *
 * Once the ratio says a systematic dominates, this is the follow-up question,
 * and it is answerable from the same ten captures: correlate the reported SH
 * against each geometry column across the set. A driver that moves the result
 * shows up as |r| near 1; one that does not, near 0.
 *
 * The common-mode split comes first because it halves the search. SH scale and
 * the pantoscopic-tilt factor are both *multipliers on both eyes*, so they can
 * only produce common-mode movement. Per-eye detection bias produces
 * differential movement. Whichever of the two dominates says which family to
 * look in before any single correlation is read.
 */
export type ShCaptureLogDriver = {
  label: string
  /** Pearson r against the per-capture mean of the two reported values. */
  r: number | null
  /**
   * Spearman rank correlation of the same pair.
   *
   * Carried next to `r` because on a ten-capture set two extreme captures can
   * manufacture a Pearson correlation on their own — and did, the first time
   * this ran: pitch read r=+0.86 "significant" and fell to +0.60 on ranks,
   * which is below the bar. A driver is only called significant when both
   * agree, so a pair of leverage points cannot name a culprit by itself.
   */
  spearman: number | null
  /** Both coefficients clear the p<0.05 two-tailed bar for this sample size. */
  isSignificant: boolean
  /**
   * Correlation with the leading driver held fixed — `null` for the leader
   * itself, and when nothing led.
   *
   * Two inputs that move together will both correlate with the result even
   * when only one causes it. That happened on the 2026-08-04 set: `scaleCv`
   * read +0.60/+0.62, right at the bar, but pitch and `scaleCv` correlate
   * +0.60 with each other, and holding pitch fixed collapsed `scaleCv` to
   * +0.26 while pitch survived at +0.66. Without this the set names two
   * culprits and neither can be ruled out.
   */
  partial: number | null
}

export type ShCaptureLogDrivers = {
  /** SD of (left+right)/2 — the part that moves both eyes together. */
  commonModeSdMm: number | null
  /** SD of (left−right) — the part that moves them apart. */
  differentialSdMm: number | null
  drivers: ShCaptureLogDriver[]
  /** Label the `partial` column is controlled for, if any driver led. */
  controlledFor: string | null
}

export type ShCaptureLogSide = {
  meanMm: number
  minMm: number
  maxMm: number
  rangeMm: number
  /** Between-capture standard deviation of the reported value. */
  sdMm: number | null
  /** Mean within-capture dispersion (σ per frame). */
  withinSigmaMm: number | null
  /** Between-capture SD that within-capture noise alone predicts. */
  predictedSdMm: number | null
  /** `sdMm / predictedSdMm`. 1 = frame noise explains the spread entirely. */
  ratio: number | null
}

export type ShCaptureLogSummary = {
  count: number
  left: ShCaptureLogSide
  right: ShCaptureLogSide
  /** The worse of the two sides — the headline number. */
  ratio: number | null
  /** What the systematic actually tracks, once the ratio says there is one. */
  drivers: ShCaptureLogDrivers
  scaleCvMeanPct: number | null
  distanceMinCm: number | null
  distanceMaxCm: number | null
  pitchMinDeg: number | null
  pitchMaxDeg: number | null
  gazeMinDeg: number | null
  gazeMaxDeg: number | null
  scaleSources: string[]
  switchedCount: number
}

/**
 * Standard error of a median is `1.2533σ/√n`, not `σ/√n` — the median pays
 * about 25% more than a mean for the same sample. The reported value is a
 * weighted median, so that is the right constant here.
 */
const MEDIAN_SE_FACTOR = 1.2533

const mean = (values: number[]) =>
  values.reduce((total, value) => total + value, 0) / values.length

/** Sample standard deviation; `null` below two values, where it is undefined. */
const standardDeviation = (values: number[]): number | null => {
  if (values.length < 2) return null
  const centre = mean(values)
  const sumSquares = values.reduce(
    (total, value) => total + (value - centre) * (value - centre),
    0
  )
  return Math.sqrt(sumSquares / (values.length - 1))
}

const summariseSide = (
  reported: number[],
  dispersions: number[],
  sampleCount: number
): ShCaptureLogSide => {
  const sdMm = standardDeviation(reported)
  const withinSigmaMm = dispersions.length > 0 ? mean(dispersions) : null
  const predictedSdMm =
    withinSigmaMm != null && sampleCount > 0
      ? (withinSigmaMm * MEDIAN_SE_FACTOR) / Math.sqrt(sampleCount)
      : null
  return {
    meanMm: mean(reported),
    minMm: Math.min(...reported),
    maxMm: Math.max(...reported),
    rangeMm: Math.max(...reported) - Math.min(...reported),
    sdMm,
    withinSigmaMm,
    predictedSdMm,
    ratio:
      sdMm != null && predictedSdMm != null && predictedSdMm > 0
        ? sdMm / predictedSdMm
        : null,
  }
}

const defined = <T>(values: Array<T | null | undefined>): NonNullable<T>[] =>
  values.filter((value): value is NonNullable<T> => value != null)

/** Pearson r; `null` below three pairs or when either side never moves. */
const correlate = (xs: number[], ys: number[]): number | null => {
  if (xs.length !== ys.length || xs.length < 3) return null
  const xMean = mean(xs)
  const yMean = mean(ys)
  const covariance = xs.reduce(
    (total, x, index) => total + (x - xMean) * (ys[index] - yMean),
    0
  )
  const xSumSquares = xs.reduce((total, x) => total + (x - xMean) ** 2, 0)
  const ySumSquares = ys.reduce((total, y) => total + (y - yMean) ** 2, 0)
  if (xSumSquares === 0 || ySumSquares === 0) return null
  return covariance / Math.sqrt(xSumSquares * ySumSquares)
}

/**
 * Critical |r| at p<0.05, two-tailed, by pair count. Ten captures is a small
 * set, so the bar is high on purpose — the point is to avoid chasing a column
 * that only looks like it moves.
 */
const CRITICAL_R: Record<number, number> = {
  3: 0.997,
  4: 0.95,
  5: 0.878,
  6: 0.811,
  7: 0.754,
  8: 0.707,
  9: 0.666,
  10: 0.632,
  11: 0.602,
  12: 0.576,
}

const criticalR = (count: number) =>
  CRITICAL_R[count] ?? (count > 12 ? 1.96 / Math.sqrt(count - 3) : 1)

/** Ranks, averaging ties, for the Spearman coefficient. */
const ranks = (values: number[]): number[] => {
  const order = values
    .map((value, index) => ({ value, index }))
    .sort((a, b) => a.value - b.value)
  const result = new Array<number>(values.length)
  let start = 0
  order.forEach((entry, position) => {
    const isLast =
      position === order.length - 1 || order[position + 1].value !== entry.value
    if (!isLast) return
    const sharedRank = (start + position) / 2 + 1
    order
      .slice(start, position + 1)
      .forEach((tied) => (result[tied.index] = sharedRank))
    start = position + 1
  })
  return result
}

const spearman = (xs: number[], ys: number[]): number | null =>
  correlate(ranks(xs), ranks(ys))

/**
 * Correlates the reported value against each per-capture input. Only entries
 * carrying a column are used for it, so one missing driver does not blank the
 * rest.
 */
const READERS: Array<
  [string, (entry: ShCaptureLogEntry, index: number) => number | undefined]
> = [
  ['scale (mm/px)', (entry) => entry.mmPerPixelMean],
  // Listed next to each other on purpose. They co-vary whenever the subject
  // watches the device, and the partial column is what separates them: gaze is
  // the proposed mechanism, pitch only its proxy.
  ['head pitch', (entry) => entry.pitchMeanDeg],
  ['eye gaze', (entry) => entry.gazeMeanDeg],
  ['distance', (entry) => entry.distanceMeanCm],
  ['scale CV', (entry) => entry.scaleCv],
  // Not a physical driver — a control. A repeat set should have no trend
  // against its own ordering; if it does, something is warming up, drifting or
  // settling, and any other correlation may just be riding on that.
  ['capture order', (_entry, index) => index + 1],
]

const summariseDrivers = (
  entries: ShCaptureLogEntry[]
): ShCaptureLogDrivers => {
  const means = entries.map((entry) => (entry.left + entry.right) / 2)
  const columns = new Map(
    READERS.map(([label, read]) => [
      label,
      entries.map((entry, index) => read(entry, index)),
    ])
  )

  const base = READERS.map(([label]) => {
    const rows = (columns.get(label) ?? [])
      .map((x, index) => ({ x, y: means[index] }))
      .filter((row): row is { x: number; y: number } => row.x != null)
    const xs = rows.map((row) => row.x)
    const ys = rows.map((row) => row.y)
    const r = correlate(xs, ys)
    const rho = spearman(xs, ys)
    const bar = criticalR(rows.length)
    return {
      label,
      r,
      spearman: rho,
      isSignificant:
        r != null && rho != null && Math.abs(r) >= bar && Math.abs(rho) >= bar,
      partial: null as number | null,
    }
  })

  // Only a driver that survived on its own is worth holding fixed.
  const leader = base
    .filter((entry) => entry.isSignificant && entry.r != null)
    .sort((a, b) => Math.abs(b.r ?? 0) - Math.abs(a.r ?? 0))[0]

  const withPartials = base.map((entry) => {
    if (!leader || entry.label === leader.label) return entry
    const control = columns.get(leader.label) ?? []
    const column = columns.get(entry.label) ?? []
    const rows = means
      .map((y, index) => ({ y, x: column[index], z: control[index] }))
      .filter(
        (row): row is { y: number; x: number; z: number } =>
          row.x != null && row.z != null
      )
    const xs = rows.map((row) => row.x)
    const ys = rows.map((row) => row.y)
    const zs = rows.map((row) => row.z)
    const rxy = correlate(xs, ys)
    const rxz = correlate(xs, zs)
    const ryz = correlate(ys, zs)
    if (rxy == null || rxz == null || ryz == null) return entry
    const denominator = Math.sqrt((1 - rxz ** 2) * (1 - ryz ** 2))
    if (denominator === 0) return entry
    return { ...entry, partial: (rxy - rxz * ryz) / denominator }
  })

  return {
    commonModeSdMm: standardDeviation(means),
    differentialSdMm: standardDeviation(
      entries.map((entry) => entry.left - entry.right)
    ),
    drivers: withPartials,
    controlledFor: leader?.label ?? null,
  }
}

/** `null` for an empty log — there is nothing to summarise before the first capture. */
export const summariseShCaptureLog = (
  entries: ShCaptureLogEntry[]
): ShCaptureLogSummary | null => {
  if (entries.length === 0) return null

  const sampleCount = mean(entries.map((entry) => entry.sampleCount))
  const left = summariseSide(
    entries.map((entry) => entry.left),
    defined(entries.map((entry) => entry.dispersionLeftMm)),
    sampleCount
  )
  const right = summariseSide(
    entries.map((entry) => entry.right),
    defined(entries.map((entry) => entry.dispersionRightMm)),
    sampleCount
  )

  const ratios = defined([left.ratio, right.ratio])
  const scaleCvs = defined(entries.map((entry) => entry.scaleCv))
  const distances = defined(entries.map((entry) => entry.distanceMeanCm))
  const pitches = defined(entries.map((entry) => entry.pitchMeanDeg))
  const gazes = defined(entries.map((entry) => entry.gazeMeanDeg))

  return {
    count: entries.length,
    left,
    right,
    ratio: ratios.length > 0 ? Math.max(...ratios) : null,
    drivers: summariseDrivers(entries),
    scaleCvMeanPct: scaleCvs.length > 0 ? mean(scaleCvs) * 100 : null,
    distanceMinCm: distances.length > 0 ? Math.min(...distances) : null,
    distanceMaxCm: distances.length > 0 ? Math.max(...distances) : null,
    pitchMinDeg: pitches.length > 0 ? Math.min(...pitches) : null,
    pitchMaxDeg: pitches.length > 0 ? Math.max(...pitches) : null,
    gazeMinDeg: gazes.length > 0 ? Math.min(...gazes) : null,
    gazeMaxDeg: gazes.length > 0 ? Math.max(...gazes) : null,
    scaleSources: [
      ...new Set(defined(entries.map((entry) => entry.scaleSource))),
    ].sort(),
    switchedCount: entries.filter((entry) => entry.scaleSourceSwitched).length,
  }
}

/**
 * Reading of the headline ratio. The threshold is deliberately loose: this is
 * meant to point at the next thing to measure, not to settle anything.
 */
export const readShCaptureLogRatio = (ratio: number | null): string => {
  if (ratio == null) return 'Not enough captures yet — repeat a few more.'
  if (ratio < 1.5) {
    return `Frame noise explains the spread (${ratio.toFixed(
      1
    )}x). More samples per capture should tighten it.`
  }
  const explainedPct = (100 / (ratio * ratio)).toFixed(0)
  return `Dominated by a per-capture systematic (${ratio.toFixed(
    1
  )}x): frame noise explains only ~${explainedPct}% of the variance. Averaging more frames cannot fix this — look at the scale, pitch and distance rows.`
}

const pad = (value: string, width: number) => value.padStart(width)

const cell = (value: number | null | undefined, width: number, digits = 2) =>
  pad(value == null ? '—' : value.toFixed(digits), width)

/** Signed, fixed-width so a column of coefficients lines up. */
export const formatShCaptureLogR = (value: number | null) =>
  value == null ? '—' : `${value < 0 ? '' : '+'}${value.toFixed(2)}`

/** The |r| bar both coefficients must clear, for display alongside them. */
export const criticalShCaptureLogR = (count: number) =>
  criticalR(count).toFixed(2)

const signed = (value: number | null) =>
  value == null ? '  —  ' : formatShCaptureLogR(value)

/**
 * Plain-text report for the Copy and Log buttons.
 *
 * Deliberately includes the **raw per-capture rows**, not just the summary: the
 * summary answers the question we already know to ask, and the rows are what
 * lets someone re-analyse the set for a question we have not thought of yet.
 */
export const formatShCaptureLogReport = (
  entries: ShCaptureLogEntry[],
  config?: ShCaptureLogConfig
): string => {
  // Only the captures taken under `config` — pooling settings is the failure
  // this key exists to stop.
  const { active, otherCount } = config
    ? selectShCaptureLogEntries(entries, config)
    : { active: entries, otherCount: 0 }
  const summary = summariseShCaptureLog(active)
  if (!summary) return 'SH capture log — no completed captures yet.'
  const configLines = config
    ? [
        ...formatShCaptureLogConfig(config),
        ...(otherCount > 0
          ? [
              `        (${otherCount} further capture${
                otherCount === 1 ? '' : 's'
              } in this session ran under other settings and are not counted here)`,
            ]
          : []),
        '',
      ]
    : ['config  (not recorded)', '']

  const header = [
    pad('#', 3),
    pad('left', 7),
    pad('right', 7),
    pad('sigL', 6),
    pad('sigR', 6),
    pad('mm/px', 9),
    pad('scaleCv', 9),
    pad('dist', 7),
    pad('pitch', 7),
    pad('gaze', 7),
    ' scale',
  ].join('')

  const rows = active.map((entry, index) =>
    [
      pad(String(index + 1), 3),
      cell(entry.left, 7),
      cell(entry.right, 7),
      cell(entry.dispersionLeftMm, 6),
      cell(entry.dispersionRightMm, 6),
      cell(entry.mmPerPixelMean, 9, 5),
      cell(entry.scaleCv == null ? null : entry.scaleCv * 100, 9, 3),
      cell(entry.distanceMeanCm, 7, 1),
      cell(entry.pitchMeanDeg, 7, 1),
      cell(entry.gazeMeanDeg, 7, 1),
      ` ${entry.scaleSource ?? '—'}${entry.scaleSourceSwitched ? '!' : ''}`,
    ].join('')
  )

  const sideRow = (
    label: string,
    read: (side: ShCaptureLogSide) => number | null,
    digits = 2
  ) =>
    `${label.padEnd(26)}${cell(read(summary.left), 7, digits)}${cell(
      read(summary.right),
      7,
      digits
    )}`

  return [
    `SH capture log — ${summary.count} captures`,
    '',
    ...configLines,
    header,
    ...rows,
    '',
    `${''.padEnd(26)}${pad('left', 7)}${pad('right', 7)}`,
    sideRow('reported mean', (side) => side.meanMm),
    sideRow('reported range', (side) => side.rangeMm),
    sideRow('between-capture SD', (side) => side.sdMm),
    sideRow('within-capture sigma', (side) => side.withinSigmaMm),
    sideRow('SD frame noise predicts', (side) => side.predictedSdMm),
    sideRow('ratio', (side) => side.ratio),
    '',
    `common-mode SD   ${cell(summary.drivers.commonModeSdMm, 6)} mm`,
    `differential SD  ${cell(summary.drivers.differentialSdMm, 6)} mm`,
    '',
    `${'driver'.padEnd(16)}${pad('r', 6)}${pad('rho', 7)}${pad('partial', 9)}`,
    ...summary.drivers.drivers.map(
      (driver) =>
        `${driver.label.padEnd(16)}${pad(signed(driver.r), 6)}${pad(
          signed(driver.spearman),
          7
        )}${pad(signed(driver.partial), 9)}${
          driver.isSignificant ? '  *significant*' : ''
        }`
    ),
    `(rho is rank-based; a driver counts only when both clear |${criticalR(
      summary.count
    ).toFixed(2)}| at n=${summary.count})`,
    summary.drivers.controlledFor
      ? `(partial holds "${summary.drivers.controlledFor}" fixed — a column that collapses there was riding on it)`
      : '(no driver cleared the bar, so nothing to control for)',
    '',
    `scale ${summary.scaleSources.join('/') || '—'}${
      summary.switchedCount > 0
        ? ` · ${summary.switchedCount} switched mid-capture`
        : ''
    }`,
    `distance ${cell(summary.distanceMinCm, 1, 1)}–${cell(
      summary.distanceMaxCm,
      1,
      1
    )} cm · pitch ${cell(summary.pitchMinDeg, 1, 1)}–${cell(
      summary.pitchMaxDeg,
      1,
      1
    )} deg · gaze ${cell(summary.gazeMinDeg, 1, 1)}–${cell(
      summary.gazeMaxDeg,
      1,
      1
    )} deg`,
    '',
    readShCaptureLogRatio(summary.ratio),
  ].join('\n')
}

/** One entry per native session; a re-review of the same capture is not a repeat. */
export const appendShCaptureLogEntry = (
  entries: ShCaptureLogEntry[],
  entry: ShCaptureLogEntry
): ShCaptureLogEntry[] =>
  entries.some((existing) => existing.sessionId === entry.sessionId)
    ? entries
    : [...entries, entry]

/**
 * Records the value as first reported, before any manual adjustment: a
 * repeatability set measures what the algorithm produces, not what an operator
 * corrected it to.
 */
export const shCaptureLogEntry = (
  sessionId: number,
  result: { left: number; right: number },
  aggregate: SHCaptureAggregateData,
  config: ShCaptureLogConfig
): ShCaptureLogEntry => ({
  sessionId,
  configKey: shCaptureLogConfigKey(config),
  left: result.left,
  right: result.right,
  sampleCount: aggregate.sampleCount,
  dispersionLeftMm: aggregate.dispersionLeft,
  dispersionRightMm: aggregate.dispersionRight,
  mmPerPixelMean: aggregate.mmPerPixelMean,
  scaleCv: aggregate.scaleCv,
  distanceMeanCm: aggregate.distanceMeanCm,
  pitchMeanDeg: aggregate.pitchMeanDeg,
  gazeMeanDeg: aggregate.gazeMeanDeg,
  scaleSource: aggregate.scaleSource,
  scaleSourceSwitched: aggregate.scaleSourceSwitched,
})
